<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <form ref="contactForm" novalidate @submit.prevent="onNext">
        <FormTitle>{{ $t('contactData.title') }}</FormTitle>
        <FormContent>
          <Space size="12px" direction="vertical">
            <p :class="$style.emailDescription">
              {{ $t('contactData.description') }}
            </p>
            <div :class="$style.formFieldsContainer">
              <div>
                <HgInput
                  ref="inputName"
                  v-model="v$.name.$model as any"
                  :updateModelOnInput="true"
                  name="name"
                  :label="t('contactData.name')"
                  :isDirty="v$.name.$dirty"
                  :isInvalid="v$.name.$invalid"
                  :class="$style.nameField"
                >
                  <template #errorMessages>
                    <p v-if="v$.name.required.$invalid">
                      {{ t('errorMessage.requiredName') }}
                    </p>
                  </template>
                </HgInput>
                <HgInput
                  ref="inputEmail"
                  v-model="v$.email.$model as any"
                  :updateModelOnInput="true"
                  name="email"
                  :label="t('contactData.emailLabel')"
                  :isDirty="v$.email.$dirty"
                  :isInvalid="v$.email.$invalid"
                  :class="$style.inputLowerCase"
                  type="email"
                >
                  <template #errorMessages>
                    <p v-if="v$.email.required.$invalid">
                      {{ t('errorMessage.requiredEmail') }}
                    </p>
                    <p v-if="v$.email.email.$invalid">
                      {{ t('errorMessage.invalidEmail') }}
                    </p>
                  </template>
                </HgInput>
                <FormCaption>
                  <i18n-t keypath="contactData.tncMessage" tag="p" :class="$style.descriptionText">
                    <template #gtcAction>
                      <HgLink
                        :href="termsAndConditionsLinks[platform][$i18n.locale]"
                        target="_blank"
                        >{{ $t('contactData.tncAction') }}</HgLink
                      >
                    </template>
                    <template #privacyAction>
                      <HgLink :href="privacyLinks[$i18n.locale]" target="_blank">{{
                        $t('contactData.legalPrivacyMessage')
                      }}</HgLink>
                    </template>
                  </i18n-t>
                  <HgCheckbox v-model="subscribeToNewsletter" :label="$t('newsletter')" />
                </FormCaption>
              </div>
              <div :class="$style.imageContainer">
                <img
                  v-if="isIs24"
                  :class="$style.image"
                  src="../../../assets/images/property-valuation/ContactTeaser_is24.webp"
                  alt="Teaser image"
                  loading="lazy"
                />
                <img
                  v-else
                  :class="$style.image"
                  src="../../../assets/images/property-valuation/ContactTeaser_hg.webp"
                  alt="Teaser image"
                  loading="lazy"
                />
                <div :class="$style.userTextContainer">
                  <span :class="$style.listItem">
                    <HgIcon icon="Check" :class="$style.checkIcon" />
                    <span>{{ t('contactData.userMessage2') }}</span>
                  </span>
                  <span :class="$style.listItem">
                    <HgIcon icon="Check" :class="$style.checkIcon" />
                    <span>{{ t('contactData.userMessage1') }}</span>
                  </span>
                  <span :class="$style.listItem">
                    <HgIcon icon="Check" :class="$style.checkIcon" />
                    <span>{{ t('contactData.userMessage3') }}</span>
                  </span>
                </div>
              </div>
            </div>
          </Space>
        </FormContent>
        <FormBottom :onNext="onNext" />
      </form>
    </TransitionWrapper>
  </FormContainer>
</template>

<script lang="ts" setup>
import { HgCheckbox, HgIcon, HgInput, HgLink, HgSupportedLanguage } from '@smg-real-estate/domus';
import { useVuelidate } from '@vuelidate/core';
import { email as emailValidator, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import {
  FormBottom,
  FormCaption,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  Space,
  TransitionWrapper,
} from '@/components/common';
import { useAuth0Client } from '@/composables/useAuth0Client';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { sanitizeInput } from '@/helpers/String';
import { postSubscribeToNewsletter, usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { ValuationFlow, privacyLinks, termsAndConditionsLinks } from '@/types';
import { Auth0VueClient, User } from '@auth0/auth0-vue';

const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  propertyDetailsData,
  selectedPropertyDetailIndex,
} = storeToRefs(propertyValuationStore);
const { t, locale } = useI18n();
const route = useRoute();
const auth0Client = useAuth0Client();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

const name = ref('');
const email = ref('');
const subscribeToNewsletter = ref(true);
const isIs24 = ref(isPlatformIs24());
const contactForm = ref();
const inputName = ref();
const inputEmail = ref();
const platform = ref(getCurrentPlatform());

const userInfo = computed((): User => {
  return (
    (auth0Client as Auth0VueClient)?.user?.value ?? {
      name: '',
      email: '',
    }
  );
});

const rules = computed(() => ({
  name: {
    required,
  },
  email: {
    email: emailValidator,
    required,
  },
}));

const v$ = useVuelidate(rules, { name, email });

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.ContactDetails);
};

const onNext = (): void => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  propertyValuationStore.setFormData({
    name: sanitizeInput(name.value),
    email: email.value,
    culture: `${locale.value}-CH`,
  });

  sendSubscribeNewsletter();
  propertyValuationStore.navigateNext(ValuationFlow.ContactDetails);
};

const sendSubscribeNewsletter = async (): Promise<void> => {
  if (subscribeToNewsletter.value) {
    await postSubscribeToNewsletter(formData.value.email, locale.value as HgSupportedLanguage);
  }
};

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_contactData',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      route.query.source?.toString() || '',
      formStep.value,
    ),
  });
  name.value = userInfo.value.name || formData.value.name;
  email.value = userInfo.value.email || formData.value.email;
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.formFieldsContainer {
  display: flex;
  gap: $space-200;
  flex-direction: column-reverse;

  @include grid-media-query(sm) {
    flex-direction: row;
    gap: $space-100;
  }
}

.imageContainer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $space-100;
  @include grid-media-query(sm) {
    flex-direction: column;
    max-width: $space-1000;
  }
}

.image {
  width: $space-800 + $space-25;
  height: $space-1000;
  @include grid-media-query(sm) {
    width: 100%;
    height: auto;
  }
}

.userTextContainer {
  @include caption;
  color: $color-grey-100;
  gap: $space-50;

  span {
    margin: 0;
  }

  [class*='hg-icon-fillable'] {
    fill: $color-grey-100;
  }

  .checkIcon {
    height: $space-75;
    width: $space-75;
    align-items: center;
  }

  .listItem {
    display: flex;
    gap: $space-25;
    align-items: center;
  }
}

.emailDescription {
  margin-top: -$space-100;
  margin-bottom: $space-25;

  @include body-2($color-grey-120);
}

.descriptionText {
  margin-top: 0;
  color: $color-black;

  @include body-3;

  &:last-child {
    margin-bottom: 0;
  }
}

.legalContentContainer {
  max-width: 35rem;
}

.legalPopupText {
  @include body-3;
}

.inputLowerCase {
  input {
    text-transform: lowercase;
  }
}

div[class*='FormBottom_wrapper_'] {
  margin-top: $space-150;
}

.nameField {
  margin-bottom: $space-150;
}
</style>
